import { useState, useMemo } from "react";
import React from 'react';
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import "../../App.css";
import Swal from "sweetalert2";
import { useCookies } from 'react-cookie';
import { Row, Col, Button } from "reactstrap";
import { default as ReactSelect } from "react-select";
import { getReceiptReport } from "../../services/ReportServices";
import { DatePicker } from 'antd';
import { getAllPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getAllBank } from "../../services/BankService";
import { getAllSalesRepresentatives } from '../../services/SalesRepresentativeServices';
import moment from 'moment';
import { displayError } from '../../helpers';
import { HandleSelect, ValueContainer, Option } from "../../components/MultiSelectControls";
import ReportExportDropdown from '../../components/ReportExportDropdown';

const { RangePicker } = DatePicker;

function ReceiptReportTable() {
    const InitialData = {
        total_pages: 0,
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(false);

    const [plants, setPlants] = React.useState([]);
    const [selectedPlant, setSelectedPlant] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedSalesrep, setSelectedSalesRep] = useState([]);
    const [salesreps, setSalesreps] = useState([]);
    const [selectedBank, setSelectedBank] = useState([]);
    const [banks, setBanks] = useState([]);

    const initialFromDate = moment().startOf('month');
    const initialToDate = moment();
    const [fromDate, setFromDate] = useState(initialFromDate);
    const [toDate, setToDate] = useState(initialToDate);

    const handleDateChange = (values) => {
        if (values && values.length === 2) {
            setFromDate(values[0]);
            setToDate(values[1]);
        }
    };

    React.useEffect(() => {
        getAllPlants(cookies)
            .then(plantList => {
                const plantOptions = plantList.plant_list
                    .filter(obj => obj.status)
                    .map(plant => ({ value: plant.id, label: plant.plant_alias }));
                setPlants(plantOptions);
                setSelectedPlant(values => [...values, ...plantOptions, ...plantsWithSelectAll]);
            });
    }, []);

    React.useEffect(() => {
        getAllCustomerMain(cookies)
            .then(customerList => {
                const customerOptions = customerList.customer_list
                    .filter(obj => obj.status)
                    .map(customer => ({ value: customer.id, label: customer.name }));
                setCustomers(customerOptions);
                setSelectedCustomer(values => [...values, ...customerOptions, ...customersWithSelectAll]);
            });
    }, []);

    React.useEffect(() => {
        getAllSalesRepresentatives(cookies)
            .then(salesRepList => {
                const salesrepOptions = salesRepList.salesrep_data
                    .filter(obj => obj.status)
                    .map(sales => ({ value: sales.id, label: sales.entity_name }));
                setSalesreps(salesrepOptions);
                setSelectedSalesRep(values => [...values, ...salesrepOptions, ...salesrepsWithSelectAll]);
            });
    }, []);

    React.useEffect(() => {
        getAllBank(cookies)
            .then(bankList => {
                const bankOptions = bankList.bank_list
                    .filter(obj => obj.status)
                    .map(bank => ({ value: bank.id, label: bank.bank_alias }));
                setBanks(bankOptions);
                setSelectedBank(values => [...values, ...bankOptions, ...banksWithSelectAll]);
            });
    }, []);

    const plantsWithSelectAll = [
        { value: "select-all", label: "Select All plants" },
        ...plants,
    ];

    const customersWithSelectAll = [
        { value: "select-all", label: "Select All customers" },
        ...customers,
    ];

    const salesrepsWithSelectAll = [
        { value: "select-all", label: "Select All sales representatives" },
        ...salesreps,
    ];

    const banksWithSelectAll = [
        { value: "select-all", label: "Select All modes" },
        ...banks,
    ];

    const onPlantSelect = (selected) => {
        HandleSelect(selected, selectedPlant, setSelectedPlant, plants, plantsWithSelectAll);
    };

    const onCustomerSelect = (selected) => {
        HandleSelect(selected, selectedCustomer, setSelectedCustomer, customers, customersWithSelectAll);
    };

    const onSalesRepSelect = (selected) => {
        HandleSelect(selected, selectedSalesrep, setSelectedSalesRep, salesreps, salesrepsWithSelectAll);
    };

    const onBankSelect = (selected) => {
        HandleSelect(selected, selectedBank, setSelectedBank, banks, banksWithSelectAll);
    };

    const areAllPlantsSelected = () => plants.every((plant) =>
        selectedPlant ? selectedPlant.some((selected) => selected.value === plant.value) : false
    );

    const areAllCustomersSelected = () => customers.every((customer) =>
        selectedCustomer ? selectedCustomer.some((selected) => selected.value === customer.value) : false
    );

    const areAllSalesRepsSelected = () => salesreps.every((salesrep) =>
        selectedSalesrep ? selectedSalesrep.some((selected) => selected.value === salesrep.value) : false
    );

    const areAllBanksSelected = () => banks.every((bank) =>
        selectedBank ? selectedBank.some((selected) => selected.value === bank.value) : false
    );

    const columns = useMemo(
        () => [
            { fieldName: 'entity_name', headerName: 'Plant Alias' },
            { fieldName: 'entity_name', headerName: 'Receipt Number' },
            { fieldName: "address", headerName: 'Receipt Date' },
            { fieldName: 'entity_name', headerName: 'Customer Name' },
            { fieldName: "mobile_number", headerName: 'Receipt Amount' },
            { fieldName: 'entity_name', headerName: 'Mode of receipt' },
            { fieldName: "address", headerName: 'Bank/Cash' },
            { fieldName: "address", headerName: 'Marketing Name' },
        ],
        [],
    );

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(c => c.headerName),
            data.records.map(r => ({
                entity_name: r.entity_name,
                phone_number: r.phone_number,
                mobile_number: r.mobile_number,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'Receipt Report',
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(col => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map(r => ({
                entity_name: r.entity_name,
                phone_number: r.phone_number,
                mobile_number: r.mobile_number,
            })),
            'Receipt.pdf'
        );
    };

    const constructFormData = () => ({
        plant_ids: areAllPlantsSelected() ? '*'
            : selectedPlant.map((plant) => plant.value).join(','),
        customer_ids: areAllCustomersSelected() ? '*'
            : selectedCustomer.map((customer) => customer.value).join(','),
        sales_rep_ids: areAllSalesRepsSelected() ? '*'
            : selectedSalesrep.map((salesrep) => salesrep.value).join(','),
        bank_ids: areAllBanksSelected() ? '*'
            : selectedBank.map((bank) => bank.value).join(','),
        from_date: fromDate.format('DD-MM-YYYY'),
        upto_date: toDate.format('DD-MM-YYYY'),
    });

    const handleGenerate = () => {
        const formData = constructFormData();
        if ((formData.plant_ids) && (formData.customer_ids) && (formData.sales_rep_ids)
            && (formData.bank_ids) && (formData.from_date) && (formData.upto_date))
        {
            setIsLoading(true);
            getReceiptReport(cookies, formData)
                .then(response => {
                    const rows = response.salesrep_data || response.receipt_report_list || [];
                    const tableData = rows.map(row => ({
                        entity_name: row.entity_name,
                        phone_number: row.phone_number,
                        mobile_number: row.mobile_number,
                        id: row.id,
                    }));
                    setData({
                        total: 0,
                        records: tableData,
                    });
                    setIsLoading(false);
                })
                .catch((error) => {
                    displayError(error.response?.data || error);
                    setIsLoading(false);
                });
        }
        else {
            Swal.fire("plant,customer,sales rep,mode of transaction,from date,upto date must not be empty.", "", "info");
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        handleGenerate();
    };

    return (
        <>
            <form className="form" onSubmit={handleSubmit}>
                <div className="container">
                    <Row>
                        <Col xl={12} lg={12} md={12}>
                            <div className="report-legacy-filters form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <div className="report-legacy-filters-fields">
                                    <ReactSelect
                                        options={plantsWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedPlant}
                                        onChange={onPlantSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please select Plant"
                                    />
                                    <ReactSelect
                                        options={customersWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedCustomer}
                                        onChange={onCustomerSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please select Customer"
                                    />
                                    <ReactSelect
                                        options={salesrepsWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedSalesrep}
                                        onChange={onSalesRepSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select SalesRep"
                                    />
                                    <ReactSelect
                                        options={banksWithSelectAll}
                                        isMulti={true}
                                        closeMenuOnSelect={false}
                                        hideSelectedOptions={false}
                                        components={{ Option, ValueContainer }}
                                        isClearable={true}
                                        value={selectedBank}
                                        onChange={onBankSelect}
                                        className="react-select-dropdown col-sm-3"
                                        placeholder="Please Select Mode of Transaction"
                                    />
                                </div>
                                <div className="report-legacy-filters-row">
                                    <RangePicker
                                        defaultValue={[initialFromDate, initialToDate]}
                                        format="DD-MM-YYYY"
                                        onChange={handleDateChange}
                                    />
                                    <div className="report-legacy-filters-spacer" aria-hidden="true" />
                                    <div className="report-legacy-actions">
                                        <ReportExportDropdown
                                            onCSV={handleCSVExport}
                                            onPDF={handlePDFExport}
                                            disabled={isLoading}
                                        />
                                        <Button
                                            color="primary"
                                            className="list-page-toolbar-generate-btn"
                                            type="button"
                                            onClick={handleGenerate}
                                            disabled={isLoading}
                                        >
                                            Generate
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </div>
            </form>
            <div className="container item-list-table-container">
                <div className="table-responsive">
                    <ReactTableWrapper
                        title="Receipt Report"
                        columns={columns}
                        data={data.records}
                        onCSVExport={handleCSVExport}
                        onPDFExport={handlePDFExport}
                        isLoading={isLoading}
                        enableTableSearch={false}
                    />
                </div>
            </div>
        </>
    );
}

export default ReceiptReportTable;
